import { createContext, useContext, useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, AlertCircle } from 'lucide-react';

const ConfirmContext = createContext(null);

/**
 * ConfirmDialogProvider — wraps app in a context that provides the async
 * confirm() function. Renders a branded portal dialog when active.
 *
 * Usage: wrap app root, then use the useConfirmDialog() hook in any component.
 */
export function ConfirmDialogProvider({ children }) {
  const { t } = useTranslation();
  const [options, setOptions] = useState(null); // null = closed
  const [typed, setTyped] = useState('');
  const resolverRef = useRef(null);

  /**
   * confirm(options) → Promise<boolean>
   * @param {{ title: string, message?: string, details?: import('react').ReactNode, variant?: 'destructive' | 'warning', confirmLabel?: string, cancelLabel?: string, requireText?: string, requireTextLabel?: string }} opts
   *   `requireText` keeps the confirm button disabled until the user types
   *   that exact string (surrounding whitespace ignored) — for deletes that
   *   must never happen by reflex.
   */
  function confirm(opts) {
    setTyped('');
    setOptions(opts);
    return new Promise((resolve) => {
      resolverRef.current = resolve;
    });
  }

  const requireText = options?.requireText ?? null;
  const typedMatches = requireText === null || typed.trim() === requireText;

  function handleConfirm() {
    if (!typedMatches) return;
    setOptions(null);
    resolverRef.current?.(true);
  }

  function handleCancel() {
    setOptions(null);
    resolverRef.current?.(false);
  }

  // Close on Escape
  useEffect(() => {
    if (!options) return;
    function onKeyDown(e) {
      if (e.key === 'Escape') handleCancel();
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [options]); // eslint-disable-line react-hooks/exhaustive-deps

  const variant = options?.variant ?? 'destructive';
  const confirmLabel = options?.confirmLabel ?? t('common.confirm');
  const cancelLabel = options?.cancelLabel ?? t('common.cancel');

  const isDestructive = variant === 'destructive';

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {options &&
        createPortal(
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            {/* Backdrop */}
            <div className="absolute inset-0 bg-black/40" onClick={handleCancel} />

            {/* Card */}
            <div className="relative bg-white dark:bg-slate-900 rounded-2xl shadow-xl dark:shadow-black/50 p-6 max-w-sm w-full mx-4">
              <div className="flex items-center gap-3 mb-4">
                {/* Icon badge */}
                <div
                  className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                    isDestructive
                      ? 'bg-red-50 dark:bg-red-500/10'
                      : 'bg-amber-50 dark:bg-amber-500/10'
                  }`}
                >
                  {isDestructive ? (
                    <AlertTriangle size={20} className="text-red-500" />
                  ) : (
                    <AlertCircle size={20} className="text-amber-500" />
                  )}
                </div>

                <div>
                  <h3 className="text-sm font-bold text-slate-800 dark:text-slate-200">
                    {options.title}
                  </h3>
                  {options.message && (
                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                      {options.message}
                    </p>
                  )}
                </div>
              </div>

              {options.details && (
                <div className="mb-4 text-xs text-slate-600 dark:text-slate-300">{options.details}</div>
              )}

              {requireText !== null && (
                <label className="block mb-4">
                  <span className="block text-xs font-semibold text-slate-600 dark:text-slate-300 mb-1">
                    {options.requireTextLabel ?? t('common.typeToConfirm', { text: requireText })}
                  </span>
                  <input
                    type="text"
                    autoFocus
                    autoComplete="off"
                    spellCheck={false}
                    dir="auto"
                    value={typed}
                    onChange={(e) => setTyped(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleConfirm(); }}
                    placeholder={requireText}
                    aria-label={options.requireTextLabel ?? t('common.typeToConfirm', { text: requireText })}
                    className="w-full px-3 py-2 text-sm rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 placeholder:text-slate-300 dark:placeholder:text-slate-600 focus:outline-none focus:ring-2 focus:ring-red-500/40"
                  />
                </label>
              )}

              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={handleCancel}
                  className="px-4 py-2 text-xs font-semibold text-slate-600 dark:text-slate-300 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-xl transition-colors"
                >
                  {cancelLabel}
                </button>
                <button
                  type="button"
                  onClick={handleConfirm}
                  disabled={!typedMatches}
                  className={`px-4 py-2 text-xs font-semibold text-white rounded-xl transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                    isDestructive
                      ? 'bg-red-500 hover:bg-red-600'
                      : 'bg-amber-500 hover:bg-amber-600'
                  }`}
                >
                  {confirmLabel}
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
    </ConfirmContext.Provider>
  );
}

/**
 * useConfirmDialog() → async confirm(options) function
 *
 * Example:
 *   const confirm = useConfirmDialog();
 *   if (!await confirm({ title: 'Delete item?', variant: 'destructive' })) return;
 */
export function useConfirmDialog() {
  return useContext(ConfirmContext);
}
